import React, { useMemo } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend 
} from "recharts";
import { Loader2, Info, Scale, Activity, Briefcase, AlertTriangle } from "lucide-react";
import { cn, coerceToText } from "../../lib/utils";
import { safeToFixed } from "../../lib/formatters";
import { Tooltip } from "../Tooltip";
import { getMetricDefinition, type MetricId } from "../../lib/metricDefinitions";

// Type-conditional dashboard gating (suppression by entity type, per ORACLE_METHODOLOGY Rule 0).
// A tile is hidden when its tooltipId is listed for the company's entity type.
// Type 5 (spin-off) is intentionally absent: its metrics apply; the engine already guards
// parent-contaminated trailing data. Type 6 (standard) shows the full set.
const HIDDEN_METRICS_BY_TYPE: Record<string, string[]> = {
  "Type 1": ["peTrailing","peForward","epsTrailing","epsForward","pegRatio","roe","roic","operatingMargin","debtToEquity","debtToEbitda","interestCoverage","fcfYield","cashConversion","freeCashFlow","wacc","roicWaccSpread","pbRatio"],
  "Type 2": ["peTrailing","peForward","epsTrailing","epsForward","pegRatio","roe","roic","operatingMargin","debtToEquity","debtToEbitda","interestCoverage","fcfYield","cashConversion","freeCashFlow","wacc","roicWaccSpread","pbRatio"],
  "Type 3": ["fcfYield","cashConversion","freeCashFlow","wacc","roicWaccSpread"],
  "Type 4": ["wacc","roicWaccSpread","roic","operatingMargin","debtToEquity","debtToEbitda","interestCoverage","fcfYield","cashConversion","freeCashFlow"],
};
function isMetricHiddenForType(tooltipId: string, entityType?: string): boolean {
  if (!entityType) return false;
  for (const [type, ids] of Object.entries(HIDDEN_METRICS_BY_TYPE)) {
    if (entityType.includes(type) && ids.includes(tooltipId)) return true;
  }
  return false;
}
import type { CompanyAnalysis, MarketContext, PricePoint } from "../../types/geminiTypes";
import type { DcfModel } from "../../hooks/useDcfModel";
import ReactMarkdown from 'react-markdown';
import { MarketIntelligencePanel } from './MarketIntelligencePanel';

export interface OverviewTabProps {
  analysis: CompanyAnalysis;
  marketContext: MarketContext | null;
  dcfModel: DcfModel | null;
  historicalData: PricePoint[];
  chartRange: string;
  chartTab: 'price' | 'comparison';
  comparisonData: any[];
  chartLoading: boolean;
  onChartRangeChange: (range: string) => void;
  onChartTabChange: (tab: 'price' | 'comparison') => void;
  useForwardMetrics: boolean;
  onToggleForwardMetrics: (useForward: boolean) => void;
}

export function OverviewTab({
  analysis,
  marketContext,
  dcfModel,
  historicalData,
  chartRange,
  chartTab,
  comparisonData,
  chartLoading,
  onChartRangeChange,
  onChartTabChange,
  useForwardMetrics,
  onToggleForwardMetrics
}: OverviewTabProps) {
  const ticker = analysis.ticker;
  const formatCurrencyValue = (millions: number | undefined | null) => {
    if (millions === undefined || millions === null || isNaN(millions)) return 'N/A';
    if (Math.abs(millions) >= 1000) {
      return `$${safeToFixed(millions / 1000, 1)}B`;
    }
    return `$${safeToFixed(millions, 0)}M`;
  };

  return (
    <div className="space-y-8">

      {/* Executive Summary */}
      {analysis.summary && (
        <div className="oi-card p-6 md:p-8">
          <h5 className="font-medium text-ink uppercase text-[10px] tracking-[0.3em] mb-5 flex items-center gap-3">
            <Briefcase className="w-4 h-4" />
            Executive Summary
          </h5>
          {analysis.entityType === 'Type 5' && (
            analysis.isType5DataContaminated ? (
              <div className="mb-5 p-4 bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] rounded-md">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-danger-600 shrink-0" />
                  <span className="text-[10px] font-medium uppercase tracking-widest text-[var(--color-danger-700)]">
                    Recent Spin-off — Valuation Not Possible
                  </span>
                </div>
                <p className="text-sm text-[var(--color-danger-700)] leading-relaxed mb-3">
                  This company was recently separated from its parent company. The financial data available
                  through our data provider reflects the <strong className="font-medium">parent company's consolidated historical
                  operations</strong> — not this entity's standalone performance. As a result, Oracle cannot
                  produce a reliable intrinsic value or cash flow projection.
                </p>
                <p className="text-sm text-[var(--color-danger-700)] leading-relaxed mb-3">
                  The <strong className="font-medium">PASS verdict is correct and deliberate</strong> — it is not a system error.
                  Warren Buffett's framework requires a verifiable track record of owner earnings before
                  any valuation can be anchored. Without 2–3 years of audited standalone annual filings,
                  any intrinsic value estimate would be fabricated.
                </p>
                <p className="text-[10px] font-medium text-[var(--color-danger-700)] uppercase tracking-wider">
                  Re-run this analysis in 12–24 months when standalone financial history is available.
                  The qualitative analysis below reflects Oracle's assessment of the business model
                  and competitive position based on available public information.
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-3 mb-5 p-3 bg-[var(--color-warning-50)] border border-[var(--color-warning-100)] rounded-md">
                <AlertTriangle className="w-4 h-4 text-warning-600 shrink-0 mt-0.5" />
                <p className="text-[10px] font-medium text-[var(--color-warning-700)] uppercase tracking-wider leading-relaxed">
                  Limited Financial History — This company has fewer than 3 years of standalone public data. Oracle has issued an analysis with wider confidence bands. Results carry additional uncertainty compared to established companies.
                </p>
              </div>
            )
          )}
          {/* Data sufficiency warning — non-Type 5 entities with fewer than 3 annual filings */}
          {analysis.entityType !== 'Type 5' && (analysis.annualFilingCount ?? 99) < 3 && (
            <div className="flex items-start gap-3 mb-5 p-3 bg-[var(--color-warning-50)] border border-[var(--color-warning-100)] rounded-md">
              <AlertTriangle className="w-4 h-4 text-warning-600 shrink-0 mt-0.5" />
              <p className="text-[10px] font-medium text-[var(--color-warning-700)] uppercase tracking-wider leading-relaxed">
                Limited Public Financial History — Fewer than 3 years of annual filings are available. Results may be less reliable than for companies with longer track records.
              </p>
            </div>
          )}
          {/* Analyst confidence indicator — shown when searchConfidenceScore < 0.5 */}
          {typeof analysis.analystSearchConfidenceScore === 'number' && analysis.analystSearchConfidenceScore < 0.5 && (
            <div className="flex items-start gap-3 mb-5 p-3 bg-surface-2 border border-ink-200 rounded-md">
              <Info className="w-4 h-4 text-ink-500 shrink-0 mt-0.5" />
              <p className="text-[10px] font-medium text-ink-500 uppercase tracking-wider leading-relaxed">
                Market Intelligence Limited — The Analyst retrieved limited current data for this company. Qualitative context is based primarily on financial filings rather than live market intelligence.
              </p>
            </div>
          )}
          <div className="text-base md:text-lg leading-[1.8] text-ink-600 font-medium">
            <ReactMarkdown>{coerceToText(analysis.summary)}</ReactMarkdown>
          </div>
        </div>
      )}

      <MarketIntelligencePanel pulse={analysis.pulse} intelligence={analysis.analystIntelligence} variant="card" />

      {/* Stock Chart & Info Grid (Moved from Market Tab) */}
      <div className="oi-card p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-1 bg-surface-2 p-1 rounded-md self-start">
            <button
              onClick={() => onChartTabChange('price')}
              className={cn(
                "px-4 py-1.5 rounded-lg text-[10px] font-medium uppercase tracking-widest transition-all",
                chartTab === 'price' 
                  ? "bg-surface text-ink border border-ink-200" 
                  : "text-ink-500 hover:text-ink"
              )}
            >
              Price
            </button>
            <button
              onClick={() => onChartTabChange('comparison')}
              className={cn(
                "px-4 py-1.5 rounded-lg text-[10px] font-medium uppercase tracking-widest transition-all",
                chartTab === 'comparison' 
                  ? "bg-surface text-ink border border-ink-200" 
                  : "text-ink-500 hover:text-ink"
              )}
            >
              Growth vs S&P 500
            </button>
          </div>
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {['YTD', '1Y', '5Y', 'Max'].map((r) => (
            <button
              key={r}
              onClick={() => onChartRangeChange(r)}
              className={cn(
                "px-4 py-1.5 rounded-md text-[10px] font-medium uppercase tracking-widest transition-colors border",
                chartRange === r 
                  ? "bg-oxblood text-paper border-oxblood" 
                  : "bg-surface-2 text-ink-600 border-ink-200 hover:text-ink hover:border-oxblood"
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[300px] w-full min-w-[200px] relative">
        {chartLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-paper/50 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-oxblood" />
          </div>
        ) : (historicalData?.length || 0) > 0 ? (
          <ResponsiveContainer width="100%" height={300} minWidth={1} minHeight={1}>
            {chartTab === 'price' ? (
              <AreaChart data={historicalData}>
                <defs>
                  <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6E1D24" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#6E1D24" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10, fill: '#4b5563' }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={30}
                />
                <YAxis 
                  domain={['auto', 'auto']} 
                  orientation="left"
                  tick={{ fontSize: 10, fill: '#6B6052' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(val: number) => `$${val}`}
                />
                <RechartsTooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  labelStyle={{ fontSize: '10px', fontWeight: 'medium', color: '#6B6052' }}
                  itemStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#000' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="price" 
                  stroke="#6E1D24" 
                  strokeWidth={2} 
                  fillOpacity={1} 
                  fill="url(#colorPrice)" 
                  animationDuration={1000}
                />
              </AreaChart>
            ) : (
              <AreaChart data={comparisonData}>
                <defs>
                  <linearGradient id="colorStock" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6E1D24" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#6E1D24" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSpy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8A7F6E" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#8A7F6E" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: '#4b5563' }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={30}
                />

                {/* Left axis — stock return scale */}
                <YAxis
                  yAxisId="stock"
                  orientation="left"
                  tick={{ fontSize: 10, fill: '#6B6052' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(val: number) => `${val > 0 ? '+' : ''}${val.toFixed(0)}%`}
                  width={60}
                />

                {/* Right axis — SPY return scale (independent) */}
                <YAxis
                  yAxisId="spy"
                  orientation="right"
                  tick={{ fontSize: 10, fill: '#8A7F6E' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(val: number) => `${val > 0 ? '+' : ''}${val.toFixed(0)}%`}
                  width={52}
                />

                <RechartsTooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  labelStyle={{ fontSize: '10px', fontWeight: 'medium', color: '#6B6052' }}
                  itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
                  formatter={(value: number, name: string) => [
                    `${value > 0 ? '+' : ''}${value.toFixed(2)}%`,
                    name === 'stockGrowth' ? ticker : 'S&P 500'
                  ]}
                />
                <Legend
                  verticalAlign="top"
                  height={36}
                  iconType="circle"
                  formatter={(value: string) => (
                    <span className="text-[10px] font-medium uppercase tracking-widest text-ink-600">
                      {value === 'stockGrowth' ? ticker : 'S&P 500'}
                    </span>
                  )}
                />

                <Area
                  yAxisId="stock"
                  type="monotone"
                  dataKey="stockGrowth"
                  name="stockGrowth"
                  stroke="#6E1D24"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorStock)"
                  animationDuration={1000}
                />
                <Area
                  yAxisId="spy"
                  type="monotone"
                  dataKey="spyGrowth"
                  name="spyGrowth"
                  stroke="#8A7F6E"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorSpy)"
                  animationDuration={1000}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-ink-600 text-sm italic">
            No historical data available
          </div>
        )}
      </div>

      {analysis.pricePerformance && (
        <div className="pt-6 border-t border-ink-200">
          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500 mb-4">
            {chartTab === 'comparison' ? 'Growth Comparison' : 'Price Performance'}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {[
              { label: '5D', value: analysis.pricePerformance["5D"], spyValue: analysis.spyPerformance?.["5D"] },
              { label: '1M', value: analysis.pricePerformance["1M"], spyValue: analysis.spyPerformance?.["1M"] },
              { label: '6M', value: analysis.pricePerformance["6M"], spyValue: analysis.spyPerformance?.["6M"] },
              { label: 'YTD', value: analysis.pricePerformance["ytd"], spyValue: analysis.spyPerformance?.["ytd"] },
              { label: '1Y', value: analysis.pricePerformance["1Y"], spyValue: analysis.spyPerformance?.["1Y"] },
              { label: '5Y', value: analysis.pricePerformance["5Y"], spyValue: analysis.spyPerformance?.["5Y"] },
            ].map((perf) => (
              <div key={perf.label} className="flex flex-col gap-1">
                <div className="text-[9px] font-medium text-ink-500 uppercase tracking-widest">{perf.label}</div>
                <div className="space-y-0.5">
                  <div className={cn(
                    "text-xs font-mono font-medium tabular-nums",
                    perf.value !== undefined && perf.value !== null ? (perf.value >= 0 ? "text-success-600" : "text-danger-600") : "text-ink-500"
                  )}>
                    {perf.value !== undefined && perf.value !== null ? `${perf.value >= 0 ? '+' : ''}${safeToFixed(perf.value, 1)}%` : 'N/A'}
                  </div>
                  {chartTab === 'comparison' && (
                    <div className={cn(
                      "text-[10px] font-mono font-medium tabular-nums flex items-center gap-1",
                      perf.spyValue !== undefined && perf.spyValue !== null ? (perf.spyValue >= 0 ? "text-ink-500" : "text-danger-600") : "text-ink-500"
                    )}>
                      <span className="text-[8px] opacity-50">SPY</span>
                      {perf.spyValue !== undefined && perf.spyValue !== null ? `${perf.spyValue >= 0 ? '+' : ''}${safeToFixed(perf.spyValue, 1)}%` : 'N/A'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>

      <div className="border-t border-ink-200 divide-y divide-ink-200">
        {/* Strategic Outlook Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 py-6 md:py-12">
          <div className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-ink-500">Normalized Earnings</div>
            <div className="text-base font-medium text-ink leading-relaxed">
              {analysis.normalizedEarnings && !isNaN(Number(analysis.normalizedEarnings)) 
                ? formatCurrencyValue(Number(analysis.normalizedEarnings)) 
                : (analysis.normalizedEarnings || 'N/A')}
            </div>
          </div>
          <div className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-ink-500">Through-Cycle Assumptions</div>
            <div className="text-base font-medium text-ink leading-relaxed">
              {analysis.throughCycleAssumptions || 'N/A'}
            </div>
          </div>
          <div className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-ink-500">10-Year Outlook</div>
            <div className="text-base font-medium text-ink leading-relaxed">
              {analysis.longTermOutlook || 'N/A'}
            </div>
          </div>
        </div>

        {/* Oracle Financial Dashboard Section */}
        {analysis.valuationBridge && (
          <div className="py-12 border-b border-ink-200">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-10 rounded-md bg-oxblood-50 flex items-center justify-center shrink-0">
                <Scale className="w-5 h-5 text-oxblood" />
              </div>
              <h3 className="font-serif text-2xl font-medium text-ink">Valuation Bridge (Per Share)</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
              {[
                { label: 'Enterprise Value', value: analysis.valuationBridge.enterpriseValue },
                { label: 'Net Debt', value: analysis.valuationBridge.netDebt, isNegative: true },
                { label: 'Equity Value', value: analysis.valuationBridge.equityValue },
                { label: 'Goodwill', value: analysis.valuationBridge.goodwill || "N/A" },
                { label: 'Tangible BV', value: analysis.valuationBridge.tangibleBookValue || "N/A", isHighlighted: (analysis.valuationBridge.tangibleBookValue?.startsWith('-')) },
                { label: 'Shares Out.', value: analysis.valuationBridge.sharesOutstanding, isHidden: true },
                { label: 'Intrinsic Value', value: analysis.valuationBridge.intrinsicValuePerShare, isHighlighted: true },
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col gap-2 relative">
                  <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500 truncate">{item.label}</div>
                  <div className={cn(
                    "text-sm font-mono font-medium tabular-nums",
                    item.isNegative ? "text-danger-600" : item.isHighlighted ? "text-oxblood px-3 py-1 bg-oxblood-50 rounded-md" : "text-ink"
                  )}>
                    {item.isNegative ? "-" : ""}{item.value}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex gap-8">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Discount Rate:</span>
                <span className="text-sm font-mono font-medium text-ink tabular-nums">{analysis.valuationBridge.discountRate}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Terminal Growth:</span>
                <span className="text-sm font-mono font-medium text-ink tabular-nums">{analysis.valuationBridge.terminalGrowthRate}</span>
              </div>
            </div>
          </div>
        )}

        <div className="py-12 border-b border-ink-200">
          <div className="flex flex-col gap-6 mb-10 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-oxblood-50 flex items-center justify-center shrink-0">
                  <Activity className="w-5 h-5 text-oxblood" />
                </div>
                <h3 className="font-serif text-2xl font-medium text-ink">Oracle Financial Dashboard</h3>
              </div>
              <div className="flex p-1 bg-surface-2 rounded-md border border-ink-200 self-start sm:self-auto">
                <button 
                  onClick={() => onToggleForwardMetrics(false)}
                  className={cn(
                    "px-4 py-1.5 sm:px-6 sm:py-2 rounded-md text-xs font-medium uppercase tracking-widest transition-all",
                    !useForwardMetrics ? "bg-surface text-ink border border-ink-200" : "text-ink-500 hover:text-ink"
                  )}
                >
                  Trailing
                </button>
                <button 
                  onClick={() => onToggleForwardMetrics(true)}
                  className={cn(
                    "px-4 py-1.5 sm:px-6 sm:py-2 rounded-md text-xs font-medium uppercase tracking-widest transition-all",
                    useForwardMetrics ? "bg-surface text-ink border border-ink-200" : "text-ink-500 hover:text-ink"
                  )}
                >
                  Forward
                </button>
              </div>
            </div>
            <div className="text-[10px] font-medium text-ink-500 italic">
              * Forward metrics based on analyst consensus estimates
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-12 gap-y-10">
            {[
              { tooltipId: 'marketCap', label: 'Market Cap', value: analysis.marketCap ? formatCurrencyValue(analysis.marketCap) : 'N/A' },
              {
                tooltipId: useForwardMetrics ? 'peForward' : 'peTrailing',
                label: useForwardMetrics ? 'Forward P/E' : 'Trailing P/E',
                value: useForwardMetrics
                  ? (typeof analysis.oracleMetrics?.peForward === 'number' && analysis.oracleMetrics.peForward > 0 ? safeToFixed(analysis.oracleMetrics.peForward, 1) : 'N/A')
                  : (typeof analysis.oracleMetrics?.peRatio === 'number' && analysis.oracleMetrics.peRatio > 0 ? safeToFixed(analysis.oracleMetrics.peRatio, 1) : 'N/A')
              },
              {
                tooltipId: useForwardMetrics ? 'epsForward' : 'epsTrailing',
                label: useForwardMetrics ? 'Forward EPS' : 'Trailing EPS',
                value: useForwardMetrics
                  ? (typeof analysis.oracleMetrics?.epsForward === 'number' ? `$${safeToFixed(analysis.oracleMetrics.epsForward, 2)}` : 'N/A')
                  : (typeof analysis.oracleMetrics?.eps === 'number' ? `$${safeToFixed(analysis.oracleMetrics.eps, 2)}` : 'N/A')
              },
              { tooltipId: 'pegRatio', label: 'PEG Ratio', value: useForwardMetrics ? 'N/A' : (typeof analysis.oracleMetrics?.pegRatio === 'number' && analysis.oracleMetrics.pegRatio > 0 ? safeToFixed(analysis.oracleMetrics.pegRatio, 2) : 'N/A') },
              { tooltipId: 'roe', label: 'ROE', value: typeof analysis.oracleMetrics?.roe === 'number' ? `${safeToFixed(analysis.oracleMetrics.roe, 1)}%` : 'N/A' },
              { tooltipId: 'roic', label: 'ROIC', value: typeof analysis.oracleMetrics?.roic === 'number' ? `${safeToFixed(analysis.oracleMetrics.roic, 1)}%` : 'N/A' },
              { tooltipId: 'wacc', label: 'WACC', value: analysis.valuationBridge?.discountRate ?? 'N/A' },
              { tooltipId: 'roicWaccSpread', label: 'ROIC − WACC', value: (() => {
                  const r = analysis.oracleMetrics?.roic;
                  const w = analysis.valuationBridge?.discountRate ? parseFloat(analysis.valuationBridge.discountRate) : NaN;
                  if (typeof r !== 'number' || isNaN(w)) return 'N/A';
                  const s = r - w;
                  return `${s >= 0 ? '+' : ''}${safeToFixed(s, 1)}%`;
                })() },
              { tooltipId: 'operatingMargin', label: 'Operating Margin', value: typeof analysis.oracleMetrics?.operatingMargin === 'number' ? `${safeToFixed(analysis.oracleMetrics.operatingMargin, 1)}%` : 'N/A' },
              { tooltipId: 'debtToEquity', label: 'Debt/Equity', value: typeof analysis.oracleMetrics?.debtToEquity === 'number' ? safeToFixed(analysis.oracleMetrics.debtToEquity, 2) : 'N/A' },
              { tooltipId: 'pbRatio', label: 'P/B Ratio', value: typeof analysis.oracleMetrics?.pbRatio === 'number' ? safeToFixed(analysis.oracleMetrics.pbRatio, 2) : 'N/A' },
              { tooltipId: 'interestCoverage', label: 'Interest Coverage', value: typeof analysis.oracleMetrics?.interestCoverage === 'number' ? `${safeToFixed(analysis.oracleMetrics.interestCoverage, 1)}x` : 'N/A' },
              { tooltipId: 'divYield', label: 'Div Yield', value: typeof analysis.dividend === 'number' ? `${safeToFixed(analysis.dividend, 2)}%` : 'N/A' },
              { tooltipId: 'shareholderYield', label: 'Shareholder Yield', value: (() => {
                  const d = typeof analysis.dividend === 'number' ? analysis.dividend : null;
                  const b = typeof analysis.avgBuybackYield === 'number' ? analysis.avgBuybackYield : null;
                  if (d === null && b === null) return 'N/A';
                  return `${safeToFixed((d ?? 0) + (b ?? 0), 2)}%`;
                })() },
              { tooltipId: 'fcfYield', label: 'FCF Yield', value: analysis.marketCap && analysis.oracleMetrics?.fcf ? `${safeToFixed((analysis.oracleMetrics.fcf / analysis.marketCap) * 100, 1)}%` : 'N/A' },
              { tooltipId: 'cashConversion', label: 'Cash Conversion', value: typeof analysis.oracleMetrics?.cashConversion === 'number' ? `${safeToFixed(analysis.oracleMetrics.cashConversion, 0)}%` : 'N/A' },
              { tooltipId: 'debtToEbitda', label: 'Debt/EBITDA', value: typeof analysis.oracleMetrics?.debtToEbitda === 'number' ? safeToFixed(analysis.oracleMetrics.debtToEbitda, 2) : 'N/A' },
              { tooltipId: 'freeCashFlow', label: 'Free Cash Flow', value: analysis.oracleMetrics?.fcf ? formatCurrencyValue(analysis.oracleMetrics.fcf) : 'N/A' },
            ].filter((item) => !isMetricHiddenForType(item.tooltipId, analysis.entityType)).map((item) => (
              <div key={item.label} className="flex flex-col gap-2 group relative">
                <div className="flex items-center gap-1">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">{item.label}</div>
                  {item.tooltipId && (() => {
                    const def = getMetricDefinition(item.tooltipId as MetricId);
                    return (
                      <Tooltip content={`${def.what} ${def.use}`}>
                        <Info className="w-3 h-3 text-ink-300 cursor-help hover:text-ink transition-colors" />
                      </Tooltip>
                    );
                  })()}
                </div>
                <div className="text-base md:text-xl font-mono font-medium text-ink tabular-nums">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
